import { useState } from "react";
import { Link } from "react-router-dom";

const FORM_URL = "https://readdy.ai/api/form/d6u32ga9puk3bj9672ng";

type FormStatus = "idle" | "submitting" | "success" | "error";

export default function ContactSection() {
  const [formStatus, setFormStatus] = useState<FormStatus>("idle");
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const message = (form.elements.namedItem("message") as HTMLTextAreaElement).value;
    if (message.length > 500) return;

    setFormStatus("submitting");
    const data = new URLSearchParams();
    const elements = form.elements;
    for (let i = 0; i < elements.length; i++) {
      const el = elements[i] as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;
      if (el.name && el.value) {
        data.append(el.name, el.value);
      }
    }

    try {
      const response = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: data.toString(),
      });
      if (response.ok) {
        setFormStatus("success");
        form.reset();
        setCharCount(0);
      } else {
        setFormStatus("error");
      }
    } catch {
      setFormStatus("error");
    }
  };

  return (
    <section id="contact-us" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        {/* Header */}
        <div className="text-center mb-16">
          <span className="text-[#1E88E5] font-heading font-semibold text-sm uppercase tracking-widest block mb-3">
            Get In Touch
          </span>
          <h2
            className="font-heading font-800 text-[#0F2040] leading-tight mb-4"
            style={{ fontSize: "clamp(1.8rem, 3.5vw, 2.75rem)", fontWeight: 800 }}
          >
            Request a Free Quote
          </h2>
          <p className="text-gray-500 font-body text-base max-w-lg mx-auto">
            Fill in the form below and we&apos;ll get back to you quickly with a no-obligation quote for window cleaning in Northfleet or Gravesend.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">
          {/* Left: Info Panel */}
          <div className="lg:col-span-2 bg-[#1A3A6B] rounded-2xl p-8 text-white flex flex-col justify-between">
            <div>
              <h3 className="font-heading font-700 text-xl mb-2" style={{ fontWeight: 700 }}>Contact Details</h3>
              <p className="text-blue-200 text-sm font-body mb-8 leading-relaxed">
                Prefer to call? Reach our friendly team directly — we&apos;re happy to help.
              </p>
              <ul className="space-y-6">
                <li>
                  <a href="tel:02084322244" className="flex items-start gap-4 group cursor-pointer">
                    <div className="w-10 h-10 flex items-center justify-center bg-white/10 rounded-xl flex-shrink-0">
                      <i className="ri-phone-fill text-[#60AEFF] text-lg"></i>
                    </div>
                    <div>
                      <span className="text-blue-200 text-xs font-body block mb-0.5">Phone</span>
                      <span className="font-heading font-600 text-white text-lg group-hover:text-[#60AEFF] transition-colors" style={{ fontWeight: 600 }}>0208 43 222 44</span>
                    </div>
                  </a>
                </li>
                <li className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-white/10 rounded-xl flex-shrink-0">
                    <i className="ri-map-pin-fill text-[#60AEFF] text-lg"></i>
                  </div>
                  <div>
                    <span className="text-blue-200 text-xs font-body block mb-0.5">Address</span>
                    <span className="font-body text-sm text-white leading-relaxed">
                      Northfleet, Kent
                    </span>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-white/10 rounded-xl flex-shrink-0">
                    <i className="ri-time-fill text-[#60AEFF] text-lg"></i>
                  </div>
                  <div>
                    <span className="text-blue-200 text-xs font-body block mb-0.5">Opening Hours</span>
                    <span className="font-body text-sm text-white">Mon – Fri: 9:00 AM – 5:00 PM</span>
                  </div>
                </li>
              </ul>
            </div>
            <div className="mt-8 pt-6 border-t border-white/15">
              <div className="flex gap-3">
                <div className="flex items-center gap-2 bg-white/10 rounded-lg px-3 py-2">
                  <i className="ri-star-fill text-yellow-400 text-sm"></i>
                  <span className="text-sm font-semibold">4.9</span>
                  <span className="text-blue-300 text-xs">Google</span>
                </div>
                <div className="flex items-center gap-2 bg-white/10 rounded-lg px-3 py-2">
                  <i className="ri-star-fill text-yellow-400 text-sm"></i>
                  <span className="text-sm font-semibold">4.8</span>
                  <span className="text-blue-300 text-xs">Facebook</span>
                </div>
              </div>
            </div>
          </div>

          {/* Right: Form */}
          <div className="lg:col-span-3 bg-[#F8FAFE] rounded-2xl p-8 border border-gray-100">
            {formStatus === "success" ? (
              <div className="flex flex-col items-center justify-center h-full text-center py-12">
                <div className="w-16 h-16 flex items-center justify-center bg-green-100 rounded-full mb-4">
                  <i className="ri-check-line text-green-500 text-2xl"></i>
                </div>
                <h3 className="font-heading font-700 text-[#0F2040] text-xl mb-2" style={{ fontWeight: 700 }}>Message Sent!</h3>
                <p className="text-gray-500 font-body text-sm max-w-sm">
                  Thank you for getting in touch. We&apos;ll get back to you within 24 hours with your free quote.
                </p>
                <button
                  onClick={() => setFormStatus("idle")}
                  className="mt-6 bg-[#1E88E5] text-white font-heading font-semibold text-sm px-6 py-3 rounded-lg cursor-pointer hover:bg-[#1565C0] transition-colors whitespace-nowrap"
                >
                  Send Another Message
                </button>
              </div>
            ) : (
              <form
                onSubmit={handleSubmit}
                data-readdy-form
                id="contact-form-home"
                className="flex flex-col gap-5"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block font-body text-sm font-medium text-gray-700 mb-1.5">Full Name *</label>
                    <input
                      name="name"
                      type="text"
                      required
                      placeholder="e.g. John Smith"
                      className="w-full bg-white border border-gray-200 rounded-lg px-4 py-3 text-sm font-body text-gray-800 placeholder-gray-400 focus:outline-none focus:border-[#1E88E5] focus:ring-2 focus:ring-[#1E88E5]/20 transition-all"
                    />
                  </div>
                  <div>
                    <label className="block font-body text-sm font-medium text-gray-700 mb-1.5">Phone Number *</label>
                    <input
                      name="phone"
                      type="tel"
                      required
                      placeholder="e.g. 07700 900 123"
                      className="w-full bg-white border border-gray-200 rounded-lg px-4 py-3 text-sm font-body text-gray-800 placeholder-gray-400 focus:outline-none focus:border-[#1E88E5] focus:ring-2 focus:ring-[#1E88E5]/20 transition-all"
                    />
                  </div>
                </div>
                <div>
                  <label className="block font-body text-sm font-medium text-gray-700 mb-1.5">Email Address *</label>
                  <input
                    name="email"
                    type="email"
                    required
                    placeholder="e.g. john@example.com"
                    className="w-full bg-white border border-gray-200 rounded-lg px-4 py-3 text-sm font-body text-gray-800 placeholder-gray-400 focus:outline-none focus:border-[#1E88E5] focus:ring-2 focus:ring-[#1E88E5]/20 transition-all"
                  />
                </div>
                <div>
                  <label className="block font-body text-sm font-medium text-gray-700 mb-1.5">Service Required</label>
                  <select
                    name="service"
                    className="w-full bg-white border border-gray-200 rounded-lg px-4 py-3 text-sm font-body text-gray-800 focus:outline-none focus:border-[#1E88E5] focus:ring-2 focus:ring-[#1E88E5]/20 transition-all cursor-pointer"
                  >
                    <option value="">Select a service...</option>
                    <option>Residential Window Cleaning</option>
                    <option>Commercial Window Cleaning</option>
                    <option>Conservatory / Glass Cleaning</option>
                    <option>Regular Maintenance Cleaning</option>
                    <option>Other / Not Sure</option>
                  </select>
                </div>
                <div>
                  <label className="block font-body text-sm font-medium text-gray-700 mb-1.5">
                    Message
                    <span className={`ml-2 text-xs ${charCount > 450 ? "text-red-500" : "text-gray-400"}`}>
                      {charCount}/500
                    </span>
                  </label>
                  <textarea
                    name="message"
                    rows={4}
                    maxLength={500}
                    onChange={(e) => setCharCount(e.target.value.length)}
                    placeholder="Tell us about your property and cleaning needs..."
                    className="w-full bg-white border border-gray-200 rounded-lg px-4 py-3 text-sm font-body text-gray-800 placeholder-gray-400 focus:outline-none focus:border-[#1E88E5] focus:ring-2 focus:ring-[#1E88E5]/20 transition-all resize-none"
                  ></textarea>
                </div>
                {formStatus === "error" && (
                  <p className="text-red-500 text-sm font-body">
                    Something went wrong. Please try again or call us directly.
                  </p>
                )}
                <button
                  type="submit"
                  disabled={formStatus === "submitting"}
                  className="w-full flex items-center justify-center gap-2 bg-[#1E88E5] hover:bg-[#1565C0] disabled:opacity-60 text-white font-heading font-semibold text-base py-4 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                >
                  {formStatus === "submitting" ? (
                    <><i className="ri-loader-4-line animate-spin"></i> Sending...</>
                  ) : (
                    <><i className="ri-send-plane-fill"></i> Get My Free Quote</>
                  )}
                </button>
                <p className="text-center text-gray-400 text-xs font-body flex items-center justify-center gap-1">
                  <i className="ri-check-line text-green-500"></i>
                  We&apos;ll respond within 24 hours · No obligation
                </p>
              </form>
            )}
          </div>
        </div>

        {/* Map */}
        <div className="mt-12 rounded-2xl overflow-hidden border border-gray-100 h-80">
          <iframe
            title="Mighty Cleaning Services Ltd location map Northfleet Gravesend Kent"
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d24843!2d0.3398!3d51.4457!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47d8b4b9aef5d10d%3A0x0!2sNorthfleet%2C%20Gravesend!5e0!3m2!1sen!2suk!4v1700000000003!5m2!1sen!2suk"
            width="100%"
            height="100%"
            style={{ border: 0 }}
            allowFullScreen
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            aria-label="Map showing Mighty Cleaning Services location in Northfleet, Kent"
          ></iframe>
        </div>
      </div>
    </section>
  );
}
